import { useState } from "react";
import { useSearchspringPLPContext } from "../../contexts/SearchspringPLPContext";
import { styled } from "styled-components";
import { colors, font } from "../../lib/sharedStyles";
import { Facet } from "../../lib/searchspringTypes";
import ChevronDown from "../Icons/ChevronDown";
import Accordion from "../Accordion";
import CheckboxIcon from "../Icons/Checkbox";
import PLPSortMenu from "./PLPSortMenu";
import { ContentfulPLPFilterAsset } from "../../lib/contentfulTypes";

const Group = styled.div<{ $isSortGroup?: boolean; }>`
  overflow: hidden;
  border-bottom: 1px solid ${colors.fender.grey03};

  @media (min-width: 1024px) {
    ${({ $isSortGroup }) => $isSortGroup && `
      display: none;
    `}
  }
`;

const GroupHeading = styled.div<{ $isExpanded: boolean; }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 0;
  cursor: pointer;
  
  span {
    ${font('Instrument Sans', 600, '16px', '22px')};
    letter-spacing: 0.16px;
    color: #000;
  }

  svg {
    transition: transform 250ms ease;

    ${({ $isExpanded }) => $isExpanded && `
      transform: rotate(180deg);
    `}
  }
`;

const OptionsList = styled.div<{ $displayType?: string; }>`
  padding-bottom: 24px;

  ${({ $displayType }) => $displayType == 'swatches' && `
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
  `}

  ${({ $displayType }) => $displayType == 'grid' && `
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  `}
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  cursor: pointer;

  span {
    ${font('Instrument Sans', 400, '14px', '20px')};
    letter-spacing: 0.14px;
    color: ${colors.fender.primaryDark};
    margin-left: 8px;
  }
`;

const GridOption = styled.div<{ $fieldName?: string; }>`
  cursor: pointer;
  // Accounts for flex gap
  width: calc(50% - 4px);
  border-radius: 2px;
  border: 1px solid ${colors.fender.grey03};
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  span {
    ${font('Instrument Sans', 400, '10px', '16px', '0.1px')};
    color: ${colors.fender.primaryDark};
  }

  img {
    max-width: 100%;
    margin-bottom: 8px;
  }

  ${({ $fieldName }) => $fieldName == 'mfield_custom_refinement_neck_shape' && `
    img {
      max-width: 45px;
    }
  `}
` ;

// Pulled from SearchspringTileSwatches. 
// Would be good to unify in the future.
const SwatchButton = styled.button<{ $isActive?: boolean; }>`
  appearance: none;
  background: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 20px;
  // Todo: reset issue SearchspringPLP PageContainer
  border: 1px solid rgba(25,26,31,0.2) !important;
  ${({ $isActive }) => $isActive && `
    border: 1px solid rgb(25,26,31) !important;
  `}
  > div {
    display: flex;
    overflow: hidden;
    width: 14px;
    height: 14px;
    border-radius: 14px; 
  }
`;

const SORT_KEY = 'sortby';

const PLPFilterList = () => {
  const [openFields, setOpenFields] = useState<string[]>([]);
  const { facets, filterPLP, filterConfig } = useSearchspringPLPContext();

  const handleOptionClick = (facet: Facet, option: any) => {
    const { field, type } = facet;

    if (field && type) {
      filterPLP(field, option, type);
    }
  }

  const handleGroupToggle = (fieldName: string) => {
    const fieldIndex = openFields.findIndex(i => i == fieldName);

    if (fieldIndex >= 0) {
      setOpenFields(openFields.filter((_, i) => i != fieldIndex));
    } else {
      setOpenFields([...openFields, fieldName]);
    }
  }
  
  return (
    <>
      {/* 
        On mobile, the sort menu acts as another filter, so it seemed
        easiest to place it here instead of exporting styled components.
        The $isSortGroup is passed to hide it on desktop, and mobile
        styling for the sort options is done in <PLPSortMenu />.
      */}
      <Group $isSortGroup>
        <GroupHeading 
          $isExpanded={openFields.includes(SORT_KEY)} 
          onClick={() => handleGroupToggle(SORT_KEY)}
        >
          <span>Sort by</span>
          <ChevronDown color={colors.fender.primaryDark} />
        </GroupHeading>
        <Accordion isOpen={openFields.includes(SORT_KEY)}>
          <OptionsList>
            <PLPSortMenu />
          </OptionsList>
        </Accordion>
      </Group>

      {facets.map(facet => {
        const { field } = facet;
        if (!field) return;
        const isExpanded = openFields.includes(field);
        
        // Display varies based on field type and Contentful configuration.
        let displayType = 'list';
        let configArray: ContentfulPLPFilterAsset[] = [];

        if (facet.field == 'variant_mfield_custom_refinement_color' && filterConfig.colorSwatches) {
          displayType = 'swatches';
          configArray = filterConfig.colorSwatches;
        }

        if (facet.field == 'variant_mfield_custom_refinement_fingerboard_material' && filterConfig.fingerboardMaterials) {
          displayType = 'swatches';
          configArray = filterConfig.fingerboardMaterials;
        }

        if (facet.field == 'variant_mfield_custom_refinement_body_material' && filterConfig.bodyMaterials) {
          displayType = 'swatches';
          configArray = filterConfig.bodyMaterials;
        }

        if (facet.field == 'mfield_custom_refinement_neck_shape' && filterConfig.neckShapes) {
          displayType = 'grid';
          configArray = filterConfig.neckShapes;
        }

        if (facet.field == 'mfield_custom_refinement_body_shape' && filterConfig.models) {
          displayType = 'grid';
          configArray = filterConfig.models;
        }

        return (
          <Group data-group={facet.label} key={field}>
            <GroupHeading $isExpanded={isExpanded} onClick={() => handleGroupToggle(field)}>
              <span>{facet.label}</span>
              <ChevronDown color={colors.fender.primaryDark} />
            </GroupHeading>
            <Accordion isOpen={isExpanded}>
              <OptionsList $displayType={displayType}>
                {facet.values?.map((option, index) => {
                  let renderKey = `${field}-${option.value}-${index}`;
                  if (facet.type == 'range-buckets') {
                    // @ts-ignore
                    renderKey == `${field}-${option.low}-${option.high}-${index}`;
                  }

                  /**
                   * 
                   * Tile display is a 2x2 grid of squares using images from Contentful.
                   * 
                   */
                  if (displayType == 'grid' && configArray.length > 0) {
                    const configEntry = configArray.find(i => i.fields.filterValue == option.value);
                    if (!configEntry) return null;

                    return (
                      <GridOption 
                        key={renderKey}
                        $fieldName={facet.field}
                        data-label={option.label}
                        onClick={() => handleOptionClick(facet, option)}
                      >
                        <img alt={option.label} src={`https:${configEntry.fields.asset.fields.file?.url}`} />
                        <span>{option.label} ({option.count})</span>
                      </GridOption>
                    );
                  }

                  /**
                   * 
                   * Swatches are a flex list of circular images.
                   * 
                   */
                  if (displayType == 'swatches' && configArray.length > 0) {
                    const configEntry = configArray.find(i => i.fields.filterValue == option.value);
                    if (!configEntry) return null;

                    return (  
                      <SwatchButton 
                        key={renderKey}
                        data-label={option.label}
                        $isActive={option.filtered}
                        onClick={() => handleOptionClick(facet, option)}
                      >
                        <div>
                          <img 
                            alt={option.label} 
                            title={option.label} 
                            src={`https:${configEntry.fields.asset.fields.file?.url}`} 
                          />
                        </div>
                      </SwatchButton>
                    );
                  }

                  /**
                   * 
                   * All other filters render as a list of checkboxes
                   * 
                   */
                  return (
                    <Option 
                      key={renderKey}
                      data-label={option.label}
                      onClick={() => handleOptionClick(facet, option)}
                    >
                      <CheckboxIcon filled={option.filtered} />
                      <span>{option.label} ({option.count})</span>
                    </Option>
                  );
                })}
              </OptionsList>
            </Accordion>
          </Group>
        );
      })}
    </>
  );
}

export default PLPFilterList;